/* eslint-disable max-lines -- Pre-existing violation */
import {skipToken} from '@reduxjs/toolkit/query';
import {
  Card,
  DataStatus,
  Dialog,
  DialogFooter,
  Form,
  FormButton,
  FormField,
  type FormControl,
  type FormSubmitHandler,
  IconButton,
  Table,
  TableRow,
  Tooltip,
  openConfirmDialog,
} from 'platform/components';
import {Grid, GridItem, HStack, Show, Text, VStack} from 'platform/foundation';
import {DateTimeFormat} from 'platform/locale';
import {z} from 'zod';

import {useState} from 'react';
import {useParams} from 'react-router-dom';

import {
  useArchiveModelMutation,
  useArchiveModelYearMutation,
  useCreateModelYearMutation,
  useGetMakeQuery,
  useGetModelQuery,
  useListModelYearQuery,
  useUpdateModelMutation,
  type ModelResponseBody,
} from '@dms/api/new-vehicle';
import {
  getNewVehicleEnumColorScheme,
  getNewVehicleEnumLabel,
  NewVehiclePageHeader,
  NewVehicleStatusFlag,
  useNewVehicleCatalogueLabels,
  refetchOnImageExpiry,
} from '@dms/features/new-vehicle';
import {vehiclesRoutes} from '@dms/routes';
import {usePermissions, VehicleStyleSelect} from '@dms/shared';

import {
  composePath,
  getApiDateString,
  parseDate,
  useNavigate,
  type ApiError,
  type Nullish,
} from 'shared/web';

import {useHeldVersion} from '../hooks/useHeldVersion';
import {useVehicleCatalogueModelOptions} from '../hooks/useVehicleCatalogueOptions';
import {
  actionsColumn,
  Cell,
  column,
  dateWindowRefinement,
  handleCatalogueError,
  isoDate,
  modelVehicleDataKey,
  optionalIsoDate,
  optionalText,
  PASSENGER_CAR,
  t,
  isStaleCatalogueVersion,
} from '../utils/catalogueUi';
import {CatalogueImageField} from './CatalogueImageField';
import {DuplicateModelYearDialog, type DuplicateModelYearTarget} from './DuplicateModelYearDialog';

const buildModelSchema = (makeKey: string | Nullish, persistedKey: string | Nullish) =>
  z.object({
    name: z.string().min(1),
    bodyStyle: optionalText,
    modelFamily: optionalText,
    tsn: optionalText,
    vehicleDataKey: modelVehicleDataKey(makeKey, persistedKey),
  });
type ModelSchema = ReturnType<typeof buildModelSchema>;
const yearSchema = z
  .object({
    code: z.string().min(1),
    validFrom: isoDate,
    validTo: optionalIsoDate,
  })
  .superRefine(dateWindowRefinement('validFrom', 'validTo'));
type ModelValues = z.infer<ModelSchema>;
type YearValues = z.infer<typeof yearSchema>;
const toModelValues = (model: ModelResponseBody | undefined): ModelValues => ({
  name: model?.name ?? '',
  bodyStyle: model?.bodyStyle ?? '',
  modelFamily: model?.modelFamily ?? '',
  tsn: model?.tsn ?? '',
  vehicleDataKey: model?.vehicleDataKey ?? '',
});

/** Only the make's own catalogue model families; a make without a key offers none. */
function ModelKeyField(props: {
  control: FormControl<ModelValues>;
  makeKey: string | Nullish;
  storedKey: string | Nullish;
  isDisabled: boolean;
}) {
  const catalogue = useVehicleCatalogueModelOptions(props.makeKey, props.storedKey);
  return (
    <FormField
      control={props.control}
      data-testid="model-vehicle-data-key"
      name="vehicleDataKey"
      type="choice"
      isSearchable
      isNotClearable
      menuInPortal
      options={catalogue.options}
      isLoading={catalogue.isLoading}
      label={t('entity.newVehicle.catalogue.vehicleCatalogueModel', 'Vehicle catalogue model')}
      helperText={
        !props.makeKey // eslint-disable-line no-nested-ternary -- Pre-existing violation
          ? t(
              'entity.newVehicle.catalogue.modelKeyNeedsMakeKey',
              'Available once the make is linked to the vehicle catalogue.'
            )
          : catalogue.isError
            ? t(
                'entity.newVehicle.catalogue.vehicleCatalogueModelsUnavailable',
                "The vehicle catalogue can't be loaded right now. You can continue with a model of your own."
              )
            : t(
                'entity.newVehicle.catalogue.modelDataKeyHelper',
                'Leave empty unless this model exists in the shared vehicle catalogue under the same make.'
              )
      }
      // A stored key stays visible (and clearable) even when the make has lost its own.
      isDisabled={props.isDisabled || (!props.makeKey && !props.storedKey)}
    />
  );
}

// eslint-disable-next-line complexity, max-lines-per-function -- Pre-existing violation
export function ModelDetail() {
  const {getBodyStyle} = useNewVehicleCatalogueLabels();
  const {modelId = ''} = useParams();
  const navigate = useNavigate();
  const [canWrite] = usePermissions({permissionKeys: ['newVehicleCatalogueWrite']});
  const modelQuery = useGetModelQuery({modelId});
  const makeQuery = useGetMakeQuery(
    modelQuery.data?.makeId ? {makeId: modelQuery.data.makeId} : skipToken
  );
  const yearsQuery = useListModelYearQuery({});
  // The model key carries its make, so it can only be judged against the make's own key.
  const modelSchema = buildModelSchema(
    makeQuery.data?.vehicleDataKey,
    modelQuery.data?.vehicleDataKey
  );
  const [updateModel] = useUpdateModelMutation();
  const [archiveModel] = useArchiveModelMutation();
  const [archiveYear] = useArchiveModelYearMutation();
  const [createYear] = useCreateModelYearMutation();
  const [isAddYearOpen, setAddYearOpen] = useState(false);
  const [duplicateTarget, setDuplicateTarget] = useState<DuplicateModelYearTarget>();
  const model = modelQuery.data;
  const make = makeQuery.data;
  const held = useHeldVersion({
    record: model,
    read: () => modelQuery.refetch().unwrap(),
    toValues: toModelValues,
  });
  const years = yearsQuery.data?.items.filter((year) => year.modelId === modelId) ?? [];
  const publicationStatus = model?.archived ? 'archived' : 'active';
  const openYear = (modelYearId: string) =>
    navigate(
      composePath(vehiclesRoutes.newVehicleCatalogueModelYear, {
        params: {modelYearId, tab: 'overview'},
      })
    );

  const saveModel: FormSubmitHandler<ModelValues> = async (values, setErrors, reset) => {
    if (!model) {
      return;
    }
    await updateModel({
      modelId,
      modelWriteRequestBody: {
        makeId: model.makeId,
        name: values.name.trim(),
        vehicleDataKey: values.vehicleDataKey || null,
        modelFamilyGroup: model.modelFamilyGroup ?? null,
        bodyStyle: values.bodyStyle || null,
        modelFamily: values.modelFamily || null,
        tsn: values.tsn || null,
        version: held.version ?? model.version,
      },
    })
      .unwrap()
      .then((saved) => {
        held.adoptVersion(saved.version, saved.id);
        reset(toModelValues(saved));
      })
      .catch((error: ApiError) => {
        handleCatalogueError(error, setErrors);
        if (isStaleCatalogueVersion(error)) {
          void held.reloadStale();
        }
      });
  };
  const addYear = async (values: YearValues) => {
    await createYear({
      modelYearWriteRequestBody: {
        modelId,
        name: values.code,
        catalogueName: `${model?.name ?? ''} ${values.code}`,
        vatRateType: 'S',
        validFrom: values.validFrom,
        validTo: values.validTo || null,
        status: 'draft',
        pricelistVersionLabel: values.code,
        version: 1,
      },
    })
      .unwrap()
      .then(() => setAddYearOpen(false))
      .catch(handleCatalogueError);
  };
  const archiveCurrentModel = () => {
    if (!model) {
      return;
    }
    openConfirmDialog({
      text: t('entity.newVehicle.confirmations.archiveModel', 'Archive model {name}?', {
        name: model.name,
      }),
      onConfirm: () =>
        void archiveModel({modelId, versionedWriteRequestBody: {version: model.version}})
          .unwrap()
          .catch(handleCatalogueError),
    });
  };

  return (
    <DataStatus
      isLoading={modelQuery.isLoading || makeQuery.isLoading}
      // A failed refetch keeps the page while it still has this model and its make.
      isError={
        (modelQuery.isError && model?.id !== modelId) ||
        (makeQuery.isError && make?.id !== model?.makeId)
      }
      errorMessage={t('entity.newVehicle.errors.modelNotFound', 'Model not found')}
      errorAction={{
        title: t('entity.newVehicle.actions.backToCatalogue', 'Back to catalogue'),
        onClick: () => navigate(vehiclesRoutes.newVehicleCatalogue),
        variant: 'secondary',
      }}
      isEmpty={!model}
    >
      <Show when={model}>
        <VStack spacing={4}>
          <NewVehiclePageHeader
            breadcrumbs={[
              {label: t('page.vehicles.title', 'Vehicles'), href: vehiclesRoutes.home},
              {
                label: t('entity.newVehicle.configurations.title', 'New vehicle configurations'),
                href: vehiclesRoutes.newVehicleConfigurations,
              },
              {
                label: t('entity.newVehicle.catalogue.title', 'Catalogue'),
                href: vehiclesRoutes.newVehicleCatalogue,
              },
              {
                label: make?.name ?? '',
                href: composePath(vehiclesRoutes.newVehicleCatalogueMake, {
                  params: {makeId: model?.makeId ?? ''},
                }),
              },
              {label: model?.name ?? ''},
            ]}
            title={model?.name ?? ''}
            status={{
              label: getNewVehicleEnumLabel('publicationStatus', publicationStatus, t),
              colorScheme: getNewVehicleEnumColorScheme('publicationStatus', publicationStatus),
              isSubtle: true,
            }}
            parameters={[
              `${t('entity.newVehicle.catalogue.bodyStyle', 'Body style')} ${
                model?.bodyStyle ? getBodyStyle(model.bodyStyle) : '—'
              }`,
              `${t('entity.newVehicle.catalogue.modelFamily', 'Model family')} ${model?.modelFamily ?? '—'}`,
              `${t('entity.newVehicle.labels.tsn', 'TSN')} ${model?.tsn ?? '—'}`,
            ]}
            actions={
              canWrite
                ? [
                    {
                      type: 'button',
                      title: t('entity.newVehicle.catalogue.addModelYear', 'Add model year'),
                      leftIcon: 'content/add',
                      onClick: () => setAddYearOpen(true),
                    },
                    {
                      type: 'dropdown-button',
                      title: t('general.labels.more'),
                      variant: 'secondary',
                      rightIcon: 'navigation/expand_more',
                      menuItems: [
                        {
                          label: t('entity.newVehicle.catalogue.archiveModel', 'Archive model'),
                          onClick: archiveCurrentModel,
                        },
                      ],
                    },
                  ]
                : []
            }
          />
          <Grid columns={3} spacing={4}>
            <GridItem span={2}>
              <Card title={t('entity.newVehicle.catalogue.modelDetails', 'Model details')}>
                {/* Keyed by the record its values come from: right after a navigation the query
                    still returns the previous model. */}
                <Form<ModelValues>
                  key={model?.id}
                  experimentalZodSchema={modelSchema}
                  defaultValues={toModelValues(model)}
                  onSubmit={saveModel}
                >
                  {(control, form) => {
                    held.formRef.current = form;
                    return (
                      <VStack spacing={3}>
                        <Grid columns={2} spacing={3}>
                          <FormField
                            control={control}
                            name="name"
                            type="text"
                            label={t('entity.vehicle.labels.model', 'Model')}
                            isRequired
                            isDisabled={!canWrite}
                          />
                          <VehicleStyleSelect
                            vehicleType={PASSENGER_CAR}
                            control={control}
                            name="bodyStyle"
                            label={t('entity.newVehicle.catalogue.bodyStyle', 'Body style')}
                            isMenuInPortal
                            isDisabled={!canWrite}
                            data-testid="newVehicleCatalogue-model"
                          />
                          <FormField
                            control={control}
                            name="modelFamily"
                            type="text"
                            label={t('entity.newVehicle.catalogue.modelFamily', 'Model family')}
                            isDisabled={!canWrite}
                          />
                          <FormField
                            control={control}
                            name="tsn"
                            type="text"
                            label={t('entity.newVehicle.labels.tsn', 'TSN')}
                            isDisabled={!canWrite}
                          />
                          <ModelKeyField
                            control={control}
                            makeKey={make?.vehicleDataKey}
                            storedKey={model?.vehicleDataKey}
                            isDisabled={!canWrite}
                          />
                        </Grid>
                        <Show when={canWrite}>
                          <HStack justify="flex-end">
                            <FormButton
                              control={control}
                              type="submit"
                              title={t('general.actions.save', 'Save')}
                            />
                          </HStack>
                        </Show>
                      </VStack>
                    );
                  }}
                </Form>
              </Card>
            </GridItem>
            <GridItem span={1}>
              <Card
                data-testid="modelImage"
                title={t('entity.newVehicle.catalogue.image', 'Image')}
              >
                {/* Right after a navigation the query still returns the previous model. */}
                <Show when={model?.id === modelId ? model : null}>
                  {(current) => (
                    <CatalogueImageField
                      owner="model"
                      ownerId={current.id}
                      image={current.imageAssetRef}
                      ownerVersion={current.version}
                      version={held.version ?? current.version}
                      onVersionChange={held.adoptVersion}
                      onStale={() => void held.reloadStale()}
                      onImageError={refetchOnImageExpiry(modelQuery)}
                      isDisabled={!canWrite || current.archived}
                    />
                  )}
                </Show>
              </Card>
            </GridItem>
          </Grid>
          <Card title={t('entity.newVehicle.catalogue.modelYears', 'Model years')}>
            <DataStatus
              isLoading={yearsQuery.isLoading}
              isError={yearsQuery.isError}
              isEmpty={!years.length}
            >
              <Table
                columns={[
                  column(t('entity.newVehicle.labels.modelYear', 'Model year'), {width: '18%'}),
                  column(t('entity.newVehicle.catalogue.status', 'Status'), {width: '14%'}),
                  column(t('entity.newVehicle.catalogue.valid', 'Valid')),
                  column(t('entity.newVehicle.catalogue.pricelistVersion', 'Pricelist version'), {
                    width: '20%',
                  }),
                  actionsColumn(),
                ]}
              >
                {years.map((year) => (
                  <TableRow key={year.id} onRowClick={() => openYear(year.id)}>
                    <Cell>{year.name}</Cell>
                    <Cell>
                      <NewVehicleStatusFlag group="modelYearStatus" value={year.status} />
                    </Cell>
                    <Cell>
                      <HStack spacing={1}>
                        <DateTimeFormat value={parseDate(year.validFrom)} format="dateShort" />
                        <Text>→</Text>
                        {year.validTo ? (
                          <DateTimeFormat value={parseDate(year.validTo)} format="dateShort" />
                        ) : (
                          <Text>{t('entity.newVehicle.catalogue.openEnded', 'Open')}</Text>
                        )}
                      </HStack>
                    </Cell>
                    <Cell>{year.pricelistVersionLabel}</Cell>
                    <Cell>
                      <div onClick={(event) => event.stopPropagation()}>
                        <HStack spacing={1} justify="flex-end">
                          <Tooltip
                            label={t(
                              'entity.newVehicle.catalogue.openModelYear',
                              'Open model year'
                            )}
                          >
                            <IconButton
                              icon="action/open_in_new"
                              aria-label={t(
                                'entity.newVehicle.catalogue.openModelYear',
                                'Open model year'
                              )}
                              onClick={() => openYear(year.id)}
                            />
                          </Tooltip>
                          <Show when={canWrite}>
                            <Tooltip
                              label={t('entity.newVehicle.catalogue.duplicate', 'Duplicate')}
                            >
                              <IconButton
                                icon="content/copy"
                                aria-label={t('entity.newVehicle.catalogue.duplicate', 'Duplicate')}
                                onClick={() =>
                                  setDuplicateTarget({
                                    modelYearId: year.id,
                                    version: year.version,
                                    modelName: model?.name ?? '',
                                    validFrom: getApiDateString(new Date()),
                                    validTo: null,
                                  })
                                }
                              />
                            </Tooltip>
                            <Tooltip label={t('general.actions.archive', 'Archive')}>
                              <IconButton
                                icon="content/archive"
                                aria-label={t('general.actions.archive', 'Archive')}
                                onClick={() =>
                                  openConfirmDialog({
                                    text: t(
                                      'entity.newVehicle.confirmations.archiveModelYear',
                                      'Archive model year {name}?',
                                      {name: year.name}
                                    ),
                                    onConfirm: () =>
                                      void archiveYear({
                                        modelYearId: year.id,
                                        versionedWriteRequestBody: {version: year.version},
                                      })
                                        .unwrap()
                                        .catch(handleCatalogueError),
                                  })
                                }
                              />
                            </Tooltip>
                          </Show>
                        </HStack>
                      </div>
                    </Cell>
                  </TableRow>
                ))}
              </Table>
            </DataStatus>
          </Card>
          <Dialog
            isOpen={isAddYearOpen}
            title={t('entity.newVehicle.catalogue.addModelYear', 'Add model year')}
            onClose={() => setAddYearOpen(false)}
            withAdditionalFooter
          >
            <Form<YearValues>
              experimentalZodSchema={yearSchema}
              defaultValues={{code: '', validFrom: '', validTo: null}}
              onSubmit={addYear}
            >
              {(control) => (
                <VStack spacing={4}>
                  <Grid columns={3} spacing={3}>
                    <FormField
                      control={control}
                      name="code"
                      type="text"
                      label={t('entity.newVehicle.catalogue.code', 'Code')}
                      isRequired
                    />
                    <FormField
                      control={control}
                      name="validFrom"
                      type="apiDate"
                      label={t('entity.newVehicle.catalogue.validFrom', 'Valid from')}
                      isRequired
                    />
                    <FormField
                      control={control}
                      name="validTo"
                      type="apiDate"
                      label={t('entity.newVehicle.catalogue.validTo', 'Valid to')}
                    />
                  </Grid>
                  <DialogFooter>
                    <HStack justify="flex-end">
                      <FormButton
                        control={control}
                        type="submit"
                        title={t('general.actions.save', 'Save')}
                      />
                    </HStack>
                  </DialogFooter>
                </VStack>
              )}
            </Form>
          </Dialog>
          <DuplicateModelYearDialog
            target={duplicateTarget}
            onClose={() => setDuplicateTarget(undefined)}
            onDuplicated={openYear}
            onStaleVersion={() => void yearsQuery.refetch()}
          />
        </VStack>
      </Show>
    </DataStatus>
  );
}
